<script>
import Customers from '@/services/Customers';

export default {
  name: 'NotificationAudienceTargeting',
  props: {
    entityLabel: {
      type: String,
      default: 'content',
    },
  },
  data() {
    return {
      recipient: 'ALL',
      systemGroups: [],
      userCreatedGroups: [],
      groupsLoading: false,
      selectedSystemGroupId: '',
      selectedUserGroup: null,
      userGroupSearch: '',
    };
  },
  computed: {
    filteredUserCreatedForSearch() {
      const q = (this.userGroupSearch || '').trim().toLowerCase();
      const list = this.userCreatedGroups;
      if (!q) {
        return list;
      }
      return list.filter((g) => (g.name || '').toLowerCase().includes(q));
    },
    selectedSystemGroup() {
      if (!this.selectedSystemGroupId) {
        return null;
      }
      return this.systemGroups.find((g) => g.group_id === this.selectedSystemGroupId) || null;
    },
    activeAudienceLabel() {
      if (this.selectedSystemGroup) {
        return (this.selectedSystemGroup.name || '').trim();
      }
      if (this.selectedUserGroup) {
        return (this.selectedUserGroup.name || '').trim();
      }
      return '';
    },
    hasActiveAudience() {
      return !!(this.selectedSystemGroupId || this.selectedUserGroup);
    },
    hasSelectableGroups() {
      return this.systemGroups.length > 0 || this.userCreatedGroups.length > 0;
    },
  },
  // Groups load on modal open via onShown() — not on component create.
  methods: {
    async loadGroups() {
      this.groupsLoading = true;
      try {
        const response = await Customers.getCustomersGroups();
        const rows = response.data.data ? response.data.data : [];
        const activeGroups = rows.filter((g) => Number(g.active) === 1);
        const isZeroLabelId = (g) => {
          const v = g && g.label_id;
          if (v === undefined || v === null || v === '') {
            return true;
          }
          return Number(v) === 0;
        };

        this.systemGroups = activeGroups.filter((g) => Number(g.system) === 1);
        this.userCreatedGroups = activeGroups.filter(
          (g) => Number(g.system) === 0 && g.type === 'static' && isZeroLabelId(g)
        );
        if (!this.hasSelectableGroups && this.recipient === 'GROUP') {
          this.recipient = 'ALL';
          this.selectedSystemGroupId = '';
          this.selectedUserGroup = null;
          this.userGroupSearch = '';
        }
      } catch (error) {
        this.systemGroups = [];
        this.userCreatedGroups = [];
        if (this.recipient === 'GROUP') {
          this.recipient = 'ALL';
          this.selectedSystemGroupId = '';
          this.selectedUserGroup = null;
          this.userGroupSearch = '';
        }
      } finally {
        this.groupsLoading = false;
      }
    },
    getSelectedGroup() {
      if (this.selectedSystemGroupId) {
        return (
          this.systemGroups.find((g) => g.group_id === this.selectedSystemGroupId)
          || this.userCreatedGroups.find((g) => g.group_id === this.selectedSystemGroupId)
          || null
        );
      }
      return this.selectedUserGroup;
    },
    getSelectedGroupId() {
      const group = this.getSelectedGroup();
      return group && group.group_id ? String(group.group_id) : '';
    },
    validate() {
      if (this.recipient === 'GROUP' && !this.getSelectedGroupId()) {
        return 'Please select an audience group.';
      }
      return null;
    },
    appendToFormData(formData) {
      formData.append('audience', (this.recipient || 'ALL').toUpperCase());

      if (this.recipient === 'GROUP') {
        const groupId = this.getSelectedGroupId();
        if (groupId) {
          formData.append('audience_group_id', groupId);
        }
      }
    },
    reset() {
      this.recipient = 'ALL';
      this.selectedSystemGroupId = '';
      this.selectedUserGroup = null;
      this.userGroupSearch = '';
    },
    async loadFromInitialAudience({ audience = 'ALL', audience_group_id = null } = {}) {
      this.reset();

      const normalizedAudience = String(audience || 'ALL').toUpperCase();
      this.recipient = normalizedAudience === 'GROUP' ? 'GROUP' : 'ALL';

      if (this.recipient !== 'GROUP' || !audience_group_id) {
        return;
      }

      if (!this.systemGroups.length && !this.userCreatedGroups.length) {
        await this.loadGroups();
      }

      if (!this.hasSelectableGroups) {
        this.recipient = 'ALL';
        return;
      }

      const groupId = String(audience_group_id);
      const systemGroup = this.systemGroups.find((g) => String(g.group_id) === groupId);
      if (systemGroup) {
        this.selectedSystemGroupId = systemGroup.group_id;
      } else {
        let userGroup = this.userCreatedGroups.find((g) => String(g.group_id) === groupId);
        if (!userGroup) {
          userGroup = {
            group_id: groupId,
            name: `Group ${groupId}`,
            type: 'static',
            active: 1,
            system: 0,
          };
          this.userCreatedGroups.push(userGroup);
        }
        this.selectedUserGroup = { ...userGroup };
      }
    },
    onShown() {
      return this.loadGroups();
    },
    onRecipientChange() {
      if (this.recipient !== 'GROUP') {
        this.selectedSystemGroupId = '';
        this.selectedUserGroup = null;
        this.userGroupSearch = '';
      }
    },
    onSystemGroupChange() {
      if (this.selectedSystemGroupId) {
        this.selectedUserGroup = null;
        this.userGroupSearch = '';
      }
    },
    pickUserCreatedGroup(group) {
      this.selectedUserGroup = { ...group };
      this.selectedSystemGroupId = '';
      this.userGroupSearch = '';
    },
    clearActiveAudience() {
      this.selectedSystemGroupId = '';
      this.selectedUserGroup = null;
      this.userGroupSearch = '';
    },
  },
};
</script>

<template>
  <div class="card notification-audience-card rounded-3 mb-3">
    <div class="card-body p-4">
      <h6 class="fw-semibold mb-1 d-flex align-items-center gap-2">
        Audience Targeting
        <b-spinner v-if="groupsLoading" small class="text-muted"></b-spinner>
      </h6>
      <p class="text-muted small mb-3">Choose who should see this {{ entityLabel }}</p>

      <div class="mb-3">
        <label class="form-label small fw-medium mb-2">Recipients</label>
        <select
          v-model="recipient"
          class="form-control form-select notification-audience-field"
          @change="onRecipientChange"
        >
          <option value="ALL">All Users</option>
          <option v-if="hasSelectableGroups" value="GROUP">Group</option>
        </select>
      </div>

      <div v-if="recipient === 'GROUP'">
        <label class="form-label small fw-medium mb-2">Audience Group</label>

        <template v-if="systemGroups.length">
          <p class="text-muted text-uppercase small fw-semibold mb-2 audience-sub-label">System groups</p>
          <select
            v-model="selectedSystemGroupId"
            class="form-select form-control notification-audience-field audience-system-select mb-3"
            :disabled="groupsLoading || !!selectedUserGroup"
            @change="onSystemGroupChange"
          >
            <option value="">
              {{ groupsLoading ? 'Loading groups…' : 'Select a system group' }}
            </option>
            <option
              v-for="group in systemGroups"
              :key="group.group_id"
              :value="group.group_id"
            >
              {{ group.name }}
            </option>
          </select>

          <div class="audience-or-divider" aria-hidden="true">
            <span></span>
            <strong>OR</strong>
            <span></span>
          </div>
        </template>

        <p class="text-muted text-uppercase small fw-semibold mb-2 audience-sub-label">
          User created groups
        </p>
        <div class="audience-user-block">
          <div
            class="audience-user-search-wrap"
            :class="{ 'audience-user-search-wrap--muted': !!selectedSystemGroupId }"
          >
            <div class="position-relative">
              <i
                class="uil uil-search position-absolute text-muted audience-search-icon"
                aria-hidden="true"
              ></i>
              <input
                v-model="userGroupSearch"
                type="text"
                class="form-control rounded-3 notification-audience-field ps-5 audience-user-search-input"
                placeholder="Search user created groups..."
                autocomplete="off"
                :disabled="groupsLoading || !!selectedSystemGroupId"
              />
              <ul
                v-if="filteredUserCreatedForSearch.length > 0 && userGroupSearch.trim() && !selectedSystemGroupId"
                class="list-group position-absolute w-100 shadow-sm mt-1 audience-suggest-list"
              >
                <li
                  v-for="group in filteredUserCreatedForSearch"
                  :key="group.group_id"
                  class="list-group-item list-group-item-action small py-2 cursor-pointer"
                  role="button"
                  @click="pickUserCreatedGroup(group)"
                >
                  {{ group.name }}
                </li>
              </ul>
            </div>
          </div>

          <div
            v-if="hasActiveAudience && activeAudienceLabel"
            class="audience-active-pill d-flex align-items-center justify-content-between gap-2 mt-3"
          >
            <span class="d-flex align-items-center min-w-0 flex-grow-1">
              <span class="audience-active-dot flex-shrink-0" aria-hidden="true"></span>
              <span class="audience-active-name small fw-semibold text-truncate">{{ activeAudienceLabel }}</span>
            </span>
            <button
              type="button"
              class="audience-active-clear flex-shrink-0"
              aria-label="Clear audience group"
              @click="clearActiveAudience"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.notification-audience-card {
  border: 1px solid #e9ecef;
  box-shadow: none !important;
}

.notification-audience-card .extra-small {
  font-size: 0.75rem;
}

.audience-sub-label {
  letter-spacing: 0.03em;
  font-size: 0.7rem;
}

.audience-search-icon {
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 1;
}

.audience-suggest-list {
  z-index: 20;
  max-height: 220px;
  overflow-y: auto;
  border-radius: 0.375rem;
}

.audience-user-search-wrap--muted .audience-user-search-input {
  opacity: 0.75;
  cursor: not-allowed !important;
}

.audience-or-divider {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  margin: 0.25rem 0 1rem;
  color: #6c757d;
}

.audience-or-divider span {
  height: 1px;
  background: #dee2e6;
}

.audience-or-divider strong {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.audience-active-pill {
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  border: 1px solid #b6d4fe;
  background-color: #e8f2fc;
}

.audience-active-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #0d6efd;
  margin-right: 0.65rem;
}

.audience-active-name {
  color: #212529;
}

.audience-active-clear {
  border: none;
  background: transparent;
  padding: 0 0.15rem;
  margin: 0;
  line-height: 1;
  font-size: 1.35rem;
  font-weight: 400;
  color: #0d6efd;
  cursor: pointer;
  border-radius: 6px;
}

.audience-active-clear:hover {
  color: #0a58ca;
  background-color: rgba(13, 110, 253, 0.1);
}

.cursor-pointer {
  cursor: pointer;
}
</style>
